<script setup>

    import { useStorage } from '@vueuse/core';
    import { useAssistantMessages } from '@/composables/assistant/useAssistantMessages';
    import { useSmartEventSource } from '@/composables/useSmartEventSource';
    import SessionsAssistantSchedule from '~/components/blocks/two-six/sessions/assistant-schedule.vue';

    // Get message helpers
    const { getDefaultMessages, getErrorMessage, getSystemMessages, getAgendaCompleteMessage, getPersonalizeMoreMessage, getSchedulePreferencesMessage, getIdleMessages } = useAssistantMessages();

    // Get SmartEventSource
    const { SmartEventSource } = useSmartEventSource();

    // Props
    const props = defineProps({
        view: {
            type: String,
            default: 'list',
        },
    });

    const full_height = ref(0);
    const userInput = ref('');
    const isStreaming = ref(false);
    const isBuilding = ref(false);
    const currentResponse = ref('');
    const currentStreamingMessage = ref(''); // Only the most recent streaming message
    const messagesContainer = ref(null);
    const textareaRef = ref(null);
    const isFixed = ref(false);

    // Idle message handling
    const lastMessageTime = ref(Date.now());
    const idleMessageInterval = ref(null);
    const idleMessageIndex = ref(0);

    // Session ID for consistent storage
    const sessionId = useStorage('assistant-session-id', Date.now() + '-' + Math.random().toString(36).substring(2, 11));
    // Messages will be initialized after session ID is set
    const messages = useStorage(`assistant-messages-${sessionId.value}`, getDefaultMessages());
    // Abort controller for canceling requests
    const abortController = ref(null);
    // Track current conversation ID for cleanup
    const currentCid = ref(null);

    // Clear message history
    const clearMessages = async () => {
        // Reset to default messages
        messages.value = getDefaultMessages();
        currentResponse.value = '';
        currentStreamingMessage.value = '';
        try {
            // Clear the session history from KV storage
            await fetch('/api/v2/assistants/clear', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId: 'user-session-' + sessionId.value,
                    cid: currentCid.value, // Include current conversation ID for cleanup
                }),
            });
            console.log('[Assistant] Session history cleared from server');
            // Reset current cid after clearing
            currentCid.value = null;
        } catch (e) {
            console.error('Failed to clear messages:', e);
        }
    };

    // Abort current streaming request
    const abortRequest = () => {
        if (abortController.value) {
            // Close the connection (works for both EventSource and SmartEventSource)
            abortController.value.close();
            abortController.value = null;

            // Get the last user message to restore to input (excluding error messages)
            const lastUserMessage = messages.value
                .slice()
                .reverse()
                .find((msg) => msg.role === 'user');

            if (lastUserMessage) {
                // Remove the last user message from the list
                const lastUserIndex = messages.value.lastIndexOf(lastUserMessage);
                messages.value.splice(lastUserIndex, 1);

                // Only restore to input if it's not the system-generated agenda build message
                if (lastUserMessage.content !== 'Build my agenda!') {
                    // Restore it to the input field
                    userInput.value = lastUserMessage.content;
                }
            }

            // Reset streaming state
            isStreaming.value = false;
            currentResponse.value = '';
            currentStreamingMessage.value = '';

            // Add recovery message after cancellation
            messages.value.push({
                role: 'assistant',
                content: getErrorMessage({ type: 'response' }),
            });
        }
    };

    // Add personalization prompt
    const personalizeMore = () => {
        if (isStreaming.value) return;
        messages.value.push({
            role: 'assistant',
            content: getPersonalizeMoreMessage(),
        });
    };

    // Add schedule preferences prompt
    const schedulePreferences = () => {
        if (isStreaming.value) return;
        messages.value.push({
            role: 'assistant',
            content: getSchedulePreferencesMessage(),
        });
    };

    // Build agenda function
    const buildAgenda = async () => {
        if (isStreaming.value) return;

        isStreaming.value = true;
        isBuilding.value = true;
        currentResponse.value = '';
        currentStreamingMessage.value = '';

        messages.value.push({
            role: 'user',
            content: 'Build my agenda!',
        });

        // Generate unique conversation ID
        const cid = `agenda-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
        currentCid.value = cid; // Store current cid for cleanup

        // Use SmartEventSource with automatic fallback
        const eventSource = new SmartEventSource(
            `/api/v2/assistants/agenda?${new URLSearchParams({
                sessionId: 'user-session-' + sessionId.value,
                cid: cid,
            })}`, { withCredentials: true }
        );

        // Store EventSource for potential abort
        abortController.value = eventSource;

        let agendaData = null;

        eventSource.addEventListener("done", () => {
            messages.value.push({
                role: 'assistant',
                content: getAgendaCompleteMessage(),
            });
            isStreaming.value = false;
            isBuilding.value = false;
            currentResponse.value = '';
            currentStreamingMessage.value = '';
            eventSource.close();
            abortController.value = null;
            return;
        })

        eventSource.addEventListener("agenda", (event) => {
            const parsed = JSON.parse(event.data);
            agendaData = parsed;
            messages.value.push({
                role: 'agenda',
                content: agendaData
            });
        })

        eventSource.addEventListener("message", (event) => {
            // Skip empty data or SSE comments
            if (!event.data || event.data.trim() === '' || event.data.startsWith(':')) {
                return;
            }
            try {
                // Skip if data looks like an SSE comment
                if (!event?.data || event.data.startsWith(':') || event.data.trim() === '') return;
                const parsed = JSON.parse(event.data);
                // Handle streaming text deltas (same as assistant endpoint)
                if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
                    // Reset the last message time when we receive actual content
                    lastMessageTime.value = Date.now();
                    currentResponse.value += parsed.delta.text;
                    // Don't update the streaming message UI - just accumulate
                }
            } catch (e) {
                // Skip invalid JSON silently
                console.error('Failed to parse SSE data:', e);
            }
        });

        eventSource.addEventListener("error", (event) => {
            console.error('[Assistant] EventSource error:', event);
            eventSource.close();
            // Only show error if we're still streaming (not user-cancelled)
            if (isStreaming.value) {
                messages.value.push({
                    role: 'assistant',
                    content: getErrorMessage({ type: 'response' }),
                });
            }
            isBuilding.value = false;
            isStreaming.value = false;
            currentResponse.value = '';
            currentStreamingMessage.value = '';
            abortController.value = null;
        })

        eventSource.onerror = (error) => {
            console.error('[Agenda Builder] EventSource error:', error);
            eventSource.close();
            if (isStreaming.value) {
                messages.value.push({
                    role: 'assistant',
                    content: getErrorMessage({ type: 'response' }),
                });
            }
            isBuilding.value = false;
            isStreaming.value = false;
            currentResponse.value = '';
            currentStreamingMessage.value = '';
            abortController.value = null;
        };
    };

    const scrollToBottom = () => {
        nextTick(() => {
            requestAnimationFrame(() => {
                if (messagesContainer.value) {
                    // Get the last li element in the messages container
                    const lastMessage = messagesContainer.value.querySelector('li.message:last-child');
                    if (lastMessage) lastMessage?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                    return;
                }
            });
        });
    };

    // Watch for changes to messages array
    watch(() => messages, () => {
        setTimeout(() => scrollToBottom(), 100);
    }, { deep: true });

    // Watch isStreaming to handle idle messages
    watch(isStreaming, (newValue) => {
        if (newValue) {
            // Start streaming - initialize idle message handling
            lastMessageTime.value = Date.now();
            currentStreamingMessage.value = '';

            const idleMessages = getIdleMessages();

            // Check for idle time every 500ms
            idleMessageInterval.value = setInterval(() => {
                const now = Date.now();
                const timeSinceLastMessage = now - lastMessageTime.value;
                // Show idle message if no content or 5+ seconds have passed
                if (isStreaming.value && (currentStreamingMessage.value === '' || timeSinceLastMessage > 2500)) {
                    if (idleMessages && idleMessages.length > 0) {
                        currentStreamingMessage.value = idleMessages[idleMessageIndex.value % idleMessages.length];
                        idleMessageIndex.value++;
                        if (idleMessageIndex.value >= idleMessages.length) {
                            idleMessageIndex.value = 0;
                        }
                    }
                    lastMessageTime.value = now; // Reset last message time to prevent immediate repeat
                }
            }, 500);
        } else {
            // Stop streaming - clear interval
            if (idleMessageInterval.value) {
                clearInterval(idleMessageInterval.value);
                idleMessageInterval.value = null;
            }
            currentStreamingMessage.value = '';
        }
    });

    const handleEnterKey = (event) => {
        if (!event.shiftKey) {
            event.preventDefault();
            sendMessage();
        }
    };

    const sendMessage = async () => {
        if (!userInput.value.trim() || isStreaming.value) return;

        const userMessage = userInput.value;

        // Add user message to local display
        messages.value.push({
            role: 'user',
            content: userMessage,
        });

        userInput.value = '';
        isStreaming.value = true;
        currentResponse.value = '';
        currentStreamingMessage.value = '';

        // Generate unique conversation ID
        const cid = `chat-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
        currentCid.value = cid; // Store current cid for cleanup

        // Use SmartEventSource with automatic fallback
        const eventSource = new SmartEventSource(
            `/api/v2/assistants/chat?${new URLSearchParams({
                input: userMessage,
                sessionId: 'user-session-' + sessionId.value,
                cid: cid,
            })}`
        );

        // Store EventSource for potential abort
        abortController.value = eventSource;

        // Variable to store formatted content
        let formattedContent = '';

        // Add a timeout to prevent hanging requests
        const timeoutId = setTimeout(() => {
            console.error('[Assistant] Request timeout - closing connection');
            eventSource.close();
            messages.value.push({
                role: 'assistant',
                content: getErrorMessage({ type: 'response' }),
            });
            isStreaming.value = false;
            currentResponse.value = '';
            currentStreamingMessage.value = '';
        }, 60000); // 60 second timeout

        eventSource.addEventListener("done", () => {
            // Check if the response is just "build"
            const responseContent = formattedContent || currentResponse.value;
            const cleanContent = responseContent.replace(/<[^>]*>/g, '').trim().toLowerCase();

            if (cleanContent === 'build') {
                // Don't add to messages, just trigger buildAgenda
                currentResponse.value = '';
                formattedContent = '';
                isStreaming.value = false;
                eventSource.close();
                clearTimeout(timeoutId);
                abortController.value = null;
                // Trigger buildAgenda after a short delay
                setTimeout(() => {
                    buildAgenda();
                }, 100);
                return;
            }

            // Only push a message if we have actual content
            if (responseContent) {
                messages.value.push({
                    role: 'assistant',
                    content: responseContent,
                });
            }
            currentResponse.value = '';
            formattedContent = '';
            isStreaming.value = false;
            eventSource.close();
            clearTimeout(timeoutId);
            abortController.value = null;
            return;
        })

        eventSource.addEventListener("message", (event) => {
            // Skip empty data or SSE comments
            if (!event.data || event.data.trim() === '' || event.data === 'undefined') {
                return;
            }

            try {
                // Skip if data looks like an SSE comment
                if (!event?.data || event.data.startsWith(':') || event.data.trim() === '') return;
                const parsed = JSON.parse(event.data);

                // Handle different event types from Anthropic API
                if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
                    // Just accumulate the response, don't update the UI
                    currentResponse.value += parsed.delta.text;
                    // Reset the timeout when we receive actual content
                    lastMessageTime.value = Date.now();
                } else if (parsed.type === 'message_complete' && parsed.formatted_content) {
                    // Store the formatted content to use when DONE is received
                    formattedContent = parsed.formatted_content;

                    // Check if the response is just "build" - if so, trigger buildAgenda
                    const cleanContent = parsed.formatted_content.replace(/<[^>]*>/g, '').trim().toLowerCase();
                    if (cleanContent === 'build') {
                        // Clear the response and trigger buildAgenda instead
                        formattedContent = '';
                        currentResponse.value = '';
                        isStreaming.value = false;
                        eventSource.close();
                        clearTimeout(timeoutId);
                        // Trigger buildAgenda after a short delay to ensure cleanup
                        setTimeout(() => {
                            buildAgenda();
                        }, 100);
                        return;
                    }
                } else if (parsed.type === 'debug' && parsed.data) {
                    // Handle debug messages - push immediately to display
                    messages.value.push({
                        role: 'debug',
                        content: parsed.data
                    });
                    // Add follow-up message for debug response
                    messages.value.push({
                        role: 'assistant',
                        content: getErrorMessage({ type: 'debug' })
                    });
                    isStreaming.value = false;
                    currentResponse.value = '';
                    currentStreamingMessage.value = '';
                } else if (parsed.type === 'content_block_start' && parsed.content_block?.type === 'text') {
                    // Handle tool results or other text content
                    if (parsed.content_block.text) {
                        currentResponse.value += parsed.content_block.text;
                    }
                } else if (parsed.type === 'error' || parsed.error) {
                    console.error('[Assistant] Server error:', parsed.error || parsed);
                    messages.value.push({
                        role: 'assistant',
                        content: getErrorMessage({ type: 'response' }),
                    });
                    isStreaming.value = false;
                    currentResponse.value = '';
                    currentStreamingMessage.value = '';
                    eventSource.close();
                    clearTimeout(timeoutId);
                    return;
                }
            } catch (e) {
                console.error('[Assistant] Failed to parse SSE data:', e);
                console.error('[Assistant] Raw data:', event.data);
            }
        });

        eventSource.addEventListener("error", (event) => {
            console.error('[Assistant] EventSource error:', event);
            eventSource.close();
            clearTimeout(timeoutId);

            // Only show error if we're still streaming (not user-cancelled)
            if (isStreaming.value) {
                messages.value.push({
                    role: 'assistant',
                    content: getErrorMessage({ type: 'response' }),
                });
            }

            isStreaming.value = false;
            currentResponse.value = '';
            currentStreamingMessage.value = '';
            abortController.value = null;
        })

        eventSource.onerror = (error) => {
            console.error('[Assistant] EventSource error:', error);
            eventSource.close();
            clearTimeout(timeoutId);
            // Only show error if we're still streaming (not user-cancelled)
            if (isStreaming.value) {
                messages.value.push({
                    role: 'assistant',
                    content: getErrorMessage({ type: 'response' }),
                });
            }
            isStreaming.value = false;
            currentResponse.value = '';
            currentStreamingMessage.value = '';
            abortController.value = null;
        };
    };

    onMounted(async () => {
        // Scroll to bottom after messages are loaded
        setTimeout(() => {
            scrollToBottom();
        }, 100);
    });
</script>

<template>
    <div class="assistant-wrapper relative w-full min-h-[40vh]" :style="`--full-height: ${full_height}px;`" role="region" aria-label="AI Assistant Chat">
        <div class="prompt-ui flex flex-col min-h-[40dvh]">
            <ul class="messages flex flex-col flex-1 gap-[1.4375rem] relative pb-[1.4375rem]" ref="messagesContainer" role="log" aria-label="Chat messages" aria-live="polite">
                <li v-for="(message, index) in messages" :key="`message-${index}`" :class="`message rounded-[var(--radius)] p-[var(--box-padding-lr)] text-[1rem] font-light leading-[1.625rem] tracking-[0.02rem] flex flex-col gap-10 ${message.role}`" role="article" :aria-label="`${message.role} message`">
                    <div class="content">
                        <SessionsAssistantSchedule v-if="message.role === 'agenda'" :schedule="message.content" :view="view"></SessionsAssistantSchedule>
                        <div v-else-if="message.role === 'assistant'" v-html="message.content" class="markdown-content" />
                        <div v-else-if="message.role === 'debug'" class="debug-content w-full">
                            <pre>{{ JSON.stringify(message.content, null, 2) }}</pre>
                        </div>
                        <div v-else>{{ message.content }}</div>
                    </div>
                    <div class="actions flex flex-col gap-[1.5625rem] lg:flex-row" v-if="message.role === 'assistant' && index === messages.length - 1">
                        <UButton @click="buildAgenda" :disabled="isStreaming" variant="outline">Build My Agenda</UButton>
                        <UButton v-if="message.content !== getPersonalizeMoreMessage()" @click="personalizeMore" :disabled="isStreaming" variant="outline">Personalize More</UButton>
                        <UButton v-if="message.content !== getSchedulePreferencesMessage()" @click="schedulePreferences" :disabled="isStreaming" variant="outline">Schedule Preferences</UButton>
                        <UButton v-if="messages.length >= 2" @click="clearMessages" :disabled="isStreaming" variant="outline">Start Over</UButton>
                    </div>
                </li>
                <li v-if="messages.length === 0" class="no-messages" role="status">{{ getSystemMessages().empty.noMessages }}</li>
                <li v-if="isStreaming" class="message rounded-[var(--radius)] p-[var(--box-padding-lr)] text-[1rem] font-light leading-[1.625rem] tracking-[0.02rem] flex flex-col assistant streaming gap-[1.8125rem]" role="article" aria-label="Assistant is typing" aria-live="assertive">
                    <h4 v-if="isBuilding" class="text-[1rem] font-semibold leading-[1.625rem] tracking-normal m-0">Building your agenda now. It may take me a few minutes!</h4>
                    <div class="content flex flex-row gap-[0.9375rem] items-center">
                        <FragmentsLoading loading />
                        <div class="streaming-content" v-html="currentStreamingMessage"></div>
                        <UButton @click="abortRequest" color="orange" variant="outline" aria-label="Abort current request">Cancel</UButton>
                    </div>
                </li>
            </ul>
            <FragmentsAssistantPrompt
                v-model:input="userInput"
                v-model:fixed="isFixed"
                :isStreaming="isStreaming"
                @sendMessage="sendMessage"
                @handleEnterKey="handleEnterKey"
                ref="textareaRef" />
        </div>
    </div>
</template>

<style>
    .assistant-wrapper .message {
        background: white;
        color: var(--teal);
    }

    .assistant-wrapper .message:last-child {
        scroll-margin-bottom: 4rem;
    }

    .assistant-wrapper .message.user {
        background: none;
        color: var(--teal);
        border: 1px solid var(--sage);
        padding: 1.25rem var(--box-padding-lr);
        font-weight: 500;
        width: fit-content;
    }

    .assistant-wrapper .message.streaming svg {
        width: 1.875rem;
        height: 1.875rem;
    }

    .assistant-wrapper .message.streaming button {
        margin-left: auto;
        font-size: 0.8125rem;
        font-weight: 400;
        letter-spacing: 0.065rem;
        text-transform: uppercase;
    }

    .assistant-wrapper .message.agenda {
        background-color: transparent;
        padding: 0;
    }

    .assistant-wrapper .message.agenda .agenda-json {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.875rem;
        line-height: 1.4;
        margin: 0;
        padding: 1rem;
        background: #fff;
        border-radius: 4px;
        overflow-x: auto;
        white-space: pre;
    }

    .assistant-wrapper .message.error {
        background: #fee;
        border: 1px solid #fcc;
        color: #c33;
    }

    .assistant-wrapper .message.debug {
        background: #f5f5f5;
        border: 1px solid #ddd;
        color: #333;
    }

    .assistant-wrapper .message.debug .debug-content pre {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.875rem;
        line-height: 1.4;
        margin: 0;
        padding: 0;
        background: transparent;
        border-radius: 4px;
        overflow-x: auto;
        white-space: pre-wrap;
        word-wrap: break-word;
        color: #333;
    }

    .assistant-wrapper .message .actions button {
        font-size: 0.8125rem;
        font-weight: 400;
        letter-spacing: 0.065rem;
        text-transform: uppercase;
    }

    .assistant-wrapper .streaming-content {
        white-space: pre-wrap;
        word-wrap: break-word;
    }

    .assistant-wrapper .typing {
        animation: blink 1s infinite;
    }

    @keyframes blink {
        0%,
        50% {
            opacity: 1;
        }
        51%,
        100% {
            opacity: 0;
        }
    }

    .assistant-wrapper .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
</style>
